import { useState } from "react";
import { Box, Typography, Skeleton } from "@mui/material";
import { AttachFile as AttachFileIcon } from "@mui/icons-material";
import { useForm } from "react-hook-form";
import TextField from "../../../Form/TextField";
import Loader from "../../../Loaders/Loader";
import { useTranslationApp } from "../../../../lib/i18next";
import { useSelectorApp, useDispatchApp } from "../../../../lib/redux";
import {
  MODAL_IMAGE_EDIT_INPUT_PLACEHOLDER,
  CREATIVES_REMAINING_SHORT,
  CREATIVES_REMAINING_SHORT_SINGULAR,
  BUTTON_SEND,
} from "../../../../i18n/keysTranslations";
import { useTheme } from "@mui/material";
import { INPUT_COLOR_BORDER } from "../../../../utils/colors";
import UploadFileButton from "../../../Buttons/UploadFileButton";
import { IMAGE_FORMATS, PLAN_TYPE_CREDITS } from "../../../../utils/constants";
import { getPathDatabaseByRole } from "../../../../actions/auth";
import { usePlanType } from "../../../Pricing/plansConfiguration";
import AttachmentChip from "./AttachmentChip";
import AttachmentsScrollRow from "./AttachmentsScrollRow";
import {
  ATTACHMENT_CHIP_VARIANTS,
  getAttachmentChipStyles,
} from "./attachmentChipTheme";

/**
 * Chat input component
 */
const ChatInput = ({ onSend, isProcessing, showSendLoading }) => {
  const { currentTypeOfPlan } = usePlanType();
  const isCreditsPlan = currentTypeOfPlan === PLAN_TYPE_CREDITS;
  const { t } = useTranslationApp();
  const theme = useTheme();
  const dispatch = useDispatchApp();
  const { control, watch, setValue } = useForm({
    defaultValues: {
      chatInput: "",
    },
  });

  const chatInput = watch("chatInput");

  // Get subscription info for creatives count
  const subscriptionPlanInfo = useSelectorApp(
    (state) => state.payments.subscriptionPlanInfo
  );
  const subscriptionPlan = useSelectorApp(
    (state) => state.payments.subscriptionPlan
  );

  // Calculate remaining creatives
  const showRestCreatives =
    !isCreditsPlan &&
    (subscriptionPlan ||
      (!subscriptionPlan && subscriptionPlanInfo?.benefits?.creatives > 0));

  const restCreatives = subscriptionPlanInfo?.usage?.creatives || 0;

  // Get userID for pathStorage
  const { userID } = dispatch(getPathDatabaseByRole());

  // Estados para manejar archivos adjuntos (igual que SetupContainer)
  const [attachmentsFiles, setAttachmentsFiles] = useState([]);
  const [fileNamesMapping, setFileNamesMapping] = useState({});
  const [pendingFilesCount, setPendingFilesCount] = useState(0);

  const onFileSelect = (files) => {
    window.pendingFiles = Array.from(files);
    setPendingFilesCount(files.length);
  };

  const onFileUpload = (newUrls) => {
    const urlsArray = Array.isArray(newUrls) ? newUrls : [newUrls];

    if (window.pendingFiles && window.pendingFiles.length > 0) {
      const newMapping = {};

      urlsArray.forEach((url) => {
        if (window.pendingFiles.length > 0) {
          const file = window.pendingFiles.shift();
          newMapping[url] = file.name;
        }
      });

      setFileNamesMapping((prev) => ({ ...prev, ...newMapping }));
    }

    setPendingFilesCount((prev) => Math.max(0, prev - urlsArray.length));

    setAttachmentsFiles((prevFiles) => [...prevFiles, ...urlsArray]);
  };

  const handleRemoveAttachment = (index) => {
    const removedFileUrl = attachmentsFiles[index];
    const newFiles = attachmentsFiles.filter((_, i) => i !== index);

    setFileNamesMapping((prev) => {
      const newMapping = { ...prev };
      delete newMapping[removedFileUrl];
      return newMapping;
    });

    setAttachmentsFiles(newFiles);
  };

  const isSendDisabled = !chatInput?.trim() || isProcessing;
  const shouldShowSendLoading = showSendLoading ?? isProcessing;
  const lightChipStyles = getAttachmentChipStyles({
    variant: ATTACHMENT_CHIP_VARIANTS.LIGHT,
    theme,
  });

  const onSubmit = () => {
    if (!isSendDisabled) {
      const attachmentUrls =
        Array.isArray(attachmentsFiles) && attachmentsFiles.length > 0
          ? attachmentsFiles
          : [];

      const attachmentFileNames = fileNamesMapping;

      onSend(chatInput, attachmentUrls, attachmentFileNames);
      setValue("chatInput", "");
      setAttachmentsFiles([]);
      setFileNamesMapping({});
      setPendingFilesCount(0);
      window.pendingFiles = [];
    }
  };

  return (
    <Box
      sx={{
        px: { xs: 2, sm: 3 },
        pt: 2,
        pb: { xs: 2, sm: 3 },
        borderTop: `1px solid ${theme.palette.divider}`,
        backgroundColor: "background.paper",
        flexShrink: 0,
      }}
      onKeyDown={(e) => {
        if ((e.code === "Enter" || e.code === "NumpadEnter") && !e.shiftKey) {
          e.preventDefault();
          onSubmit();
        }
      }}
    >
      <Box
        sx={{
          zIndex: 10,
          display: "flex",
          flexDirection: "column",
          gap: 0.4,
          border: `1px solid ${INPUT_COLOR_BORDER}`,
          borderRadius: 3,
          pt: 1.5,
          pb: 1,
          px: 1,
        }}
      >
        {/* Row 1: attachment thumbnails */}
        {(attachmentsFiles.length > 0 || pendingFilesCount > 0) && (
          <AttachmentsScrollRow>
            {attachmentsFiles.map((url, idx) => (
              <AttachmentChip
                key={`${url}-${idx}`}
                url={url}
                fileName={fileNamesMapping[url] || `imagen-${idx + 1}.png`}
                variant={ATTACHMENT_CHIP_VARIANTS.LIGHT}
                onRemove={() => handleRemoveAttachment(idx)}
              />
            ))}
            {Array.from({ length: pendingFilesCount }).map((_, index) => (
              <Box key={`skeleton-${index}`} sx={lightChipStyles.container}>
                <Skeleton
                  variant="rectangular"
                  width={22}
                  height={22}
                  sx={{
                    borderRadius: 1,
                    flexShrink: 0,
                  }}
                />
                <Skeleton
                  variant="text"
                  width={80}
                  height={16}
                  sx={{ flex: 1, minWidth: 0 }}
                />
                <Skeleton
                  variant="circular"
                  width={16}
                  height={16}
                  sx={{ flexShrink: 0 }}
                />
              </Box>
            ))}
          </AttachmentsScrollRow>
        )}

        {/* Row 2: prompt area */}
        <TextField
          name="chatInput"
          control={control}
          placeholder={t(MODAL_IMAGE_EDIT_INPUT_PLACEHOLDER)}
          disabled={isProcessing}
          multiline
          minRows={2}
          maxRows={6}
          fullWidth
          variant="filled"
          rules={{ required: false }}
          errors={{}}
          InputProps={{
            disableUnderline: true,
            sx: { alignItems: "flex-start", p: 1 },
          }}
          sx={{
            "& .MuiInputBase-root": {
              border: "none !important",
              boxShadow: "none !important",
              backgroundColor: "transparent",
            },
            ".MuiFilledInput-root": {
              borderRadius: 0,
              py: 0,
              "&:hover": {
                backgroundColor: "transparent !important",
              },
              "&:hover:not(.Mui-disabled):before": {
                borderBottom: "none !important",
              },
            },
            "& .MuiFilledInput-root:hover": {
              backgroundColor: "transparent !important",
            },
          }}
        />

        {/* Row 3: attach / send buttons */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <UploadFileButton
            disableVerificationMedia={true}
            id="upload-images-chat"
            value={false}
            name="attachedImages"
            acceptedFormats={IMAGE_FORMATS}
            multiple={true}
            pathStorage={`socialMedia/${userID}/`}
            onChange={onFileUpload}
            onFileSelect={onFileSelect}
            type="icon"
            icon={<AttachFileIcon sx={{ fontSize: "20px" }} />}
            sx={{
              minWidth: "40px !important",
              width: "40px !important",
              height: "40px !important",
              p: 0.5,
              borderRadius: 1,
            }}
          />

          {shouldShowSendLoading ? (
            <Loader hasMarginTop={false} size={18} />
          ) : (
            <Typography
              component="button"
              type="button"
              variant="body2"
              onClick={onSubmit}
              disabled={isSendDisabled}
              sx={{
                fontWeight: 700,
                border: "none",
                background: "none",
                px: 1,
                cursor: isSendDisabled ? "default" : "pointer",
                color: isSendDisabled ? "text.disabled" : "text.primary",
                "&:hover": {
                  color: isSendDisabled ? "text.disabled" : "text.primary",
                },
              }}
            >
              {t(BUTTON_SEND)}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Creatives remaining message */}
      {showRestCreatives && (
        <Typography
          variant="body2"
          sx={{
            textAlign: "center",
            color: "text.secondary",
            mt: 1,
            fontSize: { xs: 13, sm: 13 },
            opacity: 0.8,
          }}
        >
          {t(
            restCreatives === 1
              ? CREATIVES_REMAINING_SHORT_SINGULAR
              : CREATIVES_REMAINING_SHORT,
            {
              value: restCreatives > 0 ? restCreatives : 0,
            }
          )}
        </Typography>
      )}
    </Box>
  );
};

export default ChatInput;
